import { useParams, Outlet, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LocalAccessTab } from '../components/project/LocalAccessTab';
import { useKPISubscription } from '../hooks/useKPISubscription';
import { useAlarmSubscription } from '../hooks/useAlarmSubscription';
import { useDelayedSiteSummary } from '../hooks/useDelayedSiteSummary';
import { useSiteSummaryPlayback } from '../hooks/useSiteSummaryPlayback';
import { ChartModal } from '../components/modals/ChartModal';
import { DynamicKPIChart } from '../components/charts/DynamicKPIChart';
import { useDynamicChart } from '../hooks/useDynamicChart';
import { exportToCSV } from '../utils/chartExport';
import { PersistentKPIBar } from '../components/site-detail/PersistentKPIBar';
import { SiteDetailTabs } from '../components/site-detail/SiteDetailTabs';
import type { SiteDetailTab } from '../components/site-detail/SiteDetailTabs';
import OneLineDiagramTab from '../components/site-detail/tabs/OneLineDiagramTab';
import SiteOverviewDashboard from '../components/site-detail/tabs/SiteOverviewDashboard';
import { MonthlySummaryTab } from '../components/site-detail/tabs/MonthlySummaryTab';
import { StatisticsTab } from '../components/site-detail/tabs/StatisticsTab';
import api from '../services/api';
import type { ProjectDetails, GeneratorRow } from '../types/project.types';
import type { ChartModalState, AssetType } from '../types/chart.types';
import { useEffect, useState, useMemo } from 'react';
import { socketService } from '../services/socket';
import { useAuthStore } from '../store/authStore';
import { ADMIN_ROLES, ROLES_WITH_LOCAL_ACCESS, type Role } from '../constants/roles';

/** Map site status to PersistentKPIBar status values */
const kpiBarStatusMap: Record<string, 'online' | 'offline' | 'degraded' | 'fault'> = {
  online: 'online',
  offline: 'offline',
  degraded: 'degraded',
  maintenance: 'degraded',
  critical: 'fault',
};

/**
 * Context shape passed from the ProjectSummary layout down to its route
 * children (the index `ProjectSummaryContent`, `SiteAlarms`, `SiteContacts`).
 *
 * The layout owns the site fetch + live-telemetry subscriptions so the
 * PersistentKPIBar keeps updating while viewers are on Alarms/Contacts.
 */
export interface ProjectSummaryOutletContext {
  siteId: string;
  site: ProjectDetails['site'];
  alarmSummary: ProjectDetails['alarm_summary'];
  kpis: ProjectDetails['kpis'];
  kpiValues: Record<string, number | null>;
  generators: GeneratorRow[] | null;
}

export function useProjectSummaryContext(): ProjectSummaryOutletContext {
  return useOutletContext<ProjectSummaryOutletContext>();
}

/**
 * ProjectSummary — layout for the site-detail area at `/project/:id/*`.
 *
 * Responsibilities held here (and not delegated to child routes):
 *   - site fetch + 30s refresh via React Query
 *   - socket connection + `telemetry:batch` listener → PersistentKPIBar live values
 *   - `useKPISubscription` and `useAlarmSubscription` subscribe once at layout
 *     level so the KPI bar and tab-row alarm badge keep updating on every child
 *     route (Site Overview, SLD, Local Access, Alarms, Contacts)
 *   - error + loading UI (gates the Outlet so child routes never mount with
 *     partial data)
 *
 * Children render their own `SiteDetailTabs` — the component is route-aware
 * and highlights Alarms/Contacts/Market by URL while falling back to the
 * `activeInPlaceTab` prop on the index route. Each passes the site it reads from
 * this layout, which the Market tab's gate needs.
 */
export function ProjectSummary() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    try { socketService.connect(); } catch { /* socket.io handles retry */ }
  }, []);

  useKPISubscription(id || '', !!id);
  useAlarmSubscription(id);

  // Group-1 (1 Hz): per-second raw fields played from telemetry:batch samples[].
  // useSiteSummaryPlayback is called FIRST so its socket handler is registered
  // before useDelayedSiteSummary's — the test mock (Map.set) retains the last
  // writer, which is useDelayedSiteSummary. See djin-cloud#552.
  const group1Playback = useSiteSummaryPlayback(id);

  // Group-2 (5 s batch cadence): derived / aggregated fields from site_summary
  // (site_power_factor, utilization, counts, meter_*, generators[]).
  // Per-key merge of each arriving telemetry:batch (djin-cloud#372 contract).
  const group2Summary = useDelayedSiteSummary(id);

  // Merge: Group-1 wins (spread last) so a 5 s batch update never clobbers
  // the 1 Hz playback value for the fast-moving site metrics.
  const latestSiteSummary = useMemo(
    () => ({ ...group2Summary, ...group1Playback }),
    [group2Summary, group1Playback],
  );

  const kpiValues = useMemo((): Record<string, number | null> => {
    // Prefer live telemetry site_summary over static query data
    if (Object.keys(latestSiteSummary).length > 0) {
      // Helper: pick a numeric value, else null. Guards against non-numeric
      // values (e.g. the `generators` array) that share the same bag.
      const pickNum = (key: string): number | null => {
        const v = latestSiteSummary[key];
        return typeof v === 'number' ? v : null;
      };
      return {
        total_load_kw: pickNum('total_load_kw'),
        total_reactive_power_kvar: pickNum('total_reactive_power_kvar'),
        total_apparent_power_kva: pickNum('total_apparent_power_kva'),
        capacity_online_kw: pickNum('capacity_online_kw'),
        capacity_offline_kw: pickNum('capacity_offline_kw'),
        capacity_total_kw: pickNum('capacity_total_kw'),
        site_utilization_pct: pickNum('site_utilization_pct'),
        bess_available_kwh: pickNum('bess_available_kwh'),
        bess_available_kw: pickNum('bess_available_kw'),
        fuel_consumption_mmbtu_h: pickNum('fuel_consumption_mmbtu_h'),
        grid_frequency_hz: pickNum('grid_frequency_hz'),
        // Phase 3 (djin-cloud#67) — site_summary extensions powering the
        // Battery Storage and Site Meter cards. Backend emits these from
        // augmentSiteSummary; the wire keeps `?? null` semantics.
        site_power_factor: pickNum('site_power_factor'),
        bess_online_count: pickNum('bess_online_count'),
        bess_total_count: pickNum('bess_total_count'),
        meter_power_factor: pickNum('meter_power_factor'),
        meter_voltage_a_v: pickNum('meter_voltage_a_v'),
        meter_voltage_b_v: pickNum('meter_voltage_b_v'),
        meter_voltage_c_v: pickNum('meter_voltage_c_v'),
        meter_active_power_kw: pickNum('meter_active_power_kw'),
        // Phase 4 (djin-cloud#67) — generator count fields for the Generators
        // card header. The `generators` array itself is a separate memo.
        generators_online_count: pickNum('generators_online_count'),
        generators_total_count: pickNum('generators_total_count'),
      };
    }
    return {};
  }, [latestSiteSummary]);

  /**
   * Phase 4 (djin-cloud#67): extract `generators` as a typed array. Kept as a
   * separate memo from `kpiValues` (which is `Record<string, number | null>`)
   * to preserve array typing. Returns `null` for unknown roster (wire sends
   * null) and `[]` for known-zero or "awaiting data" — both are empty-state
   * branches on the GeneratorsCard.
   */
  const generators = useMemo<GeneratorRow[] | null>(() => {
    const gens = latestSiteSummary.generators;
    if (gens == null) return null;
    if (!Array.isArray(gens)) return null; // defensive — sanitizer should guarantee array shape
    return gens as GeneratorRow[];
  }, [latestSiteSummary]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['project', id],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: ProjectDetails }>(`/v1/projects/${id}`);
      return response.data.data;
    },
    refetchInterval: 30000,
  });

  const userRole = useAuthStore((s) => s.user?.role);
  const isAdmin = !!userRole && ADMIN_ROLES.includes(userRole as Role);

  if (isError) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-lg font-semibold text-ems-text-primary">
            {status === 403 ? 'Access Denied' : 'Site Not Found'}
          </p>
          <p className="text-sm text-ems-text-muted mt-1">
            {status === 403
              ? 'You do not have permission to view this site.'
              : 'The requested site could not be loaded.'}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading || !data || !id) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-ems-text-muted">Loading project details...</p>
      </div>
    );
  }

  const { site, alarm_summary, kpis } = data;

  const outletContext: ProjectSummaryOutletContext = {
    siteId: id,
    site,
    alarmSummary: alarm_summary,
    kpis,
    kpiValues,
    generators,
  };

  // flex-col + min-h-full continues the flex chain from AppLayout so that
  // nested children with `flex-1 min-h-0` (SiteAlarms, SiteContacts) can
  // flex-fill the remaining vertical space under the KPI bar. min-h (not h)
  // lets tall in-place content on the index route grow past the viewport,
  // with AppLayout's `<main overflow-y-auto>` scrolling above.
  return (
    <div className="flex flex-col min-h-full">
      <PersistentKPIBar
        siteName={site.site_name}
        status={kpiBarStatusMap[site.status] || 'offline'}
        kpis={kpiValues}
        adminSettingsHref={isAdmin ? `/admin/sites/${id}/settings` : undefined}
      />
      <Outlet context={outletContext} />
    </div>
  );
}

/**
 * ProjectSummaryContent — index-route body for `/project/:id`.
 *
 * Renders the tab row and the in-place tab branches (Site Overview,
 * One-Line Diagram, Local Access, plus the currently-hidden Monthly Summary /
 * Statistics tabs from #148). Owns its own tab state
 * because the in-place tabs share a single URL.
 *
 * The chart modal lives here because only in-place tabs invoke it; Alarms
 * and Contacts never open it.
 */
export function ProjectSummaryContent() {
  const { siteId, site, alarmSummary, kpis, kpiValues, generators } =
    useProjectSummaryContext();
  const [activeTab, setActiveTab] = useState<SiteDetailTab>('site-overview');
  const userRole = useAuthStore((s) => s.user?.role);

  const [chartModal, setChartModal] = useState<ChartModalState>({
    isOpen: false, kpiName: null, assetType: null, siteId: null, siteName: null,
  });

  // Retained for KPI click-to-chart — wired in where tabs render KPI cards.
  const handleKPIClick = (kpiName: string, assetType: AssetType) => {
    setChartModal({ isOpen: true, kpiName, assetType, siteId, siteName: site.site_name });
  };
  void handleKPIClick;

  const handleCloseModal = () => {
    setChartModal({ isOpen: false, kpiName: null, assetType: null, siteId: null, siteName: null });
  };

  const chartData = useDynamicChart({
    siteId: chartModal.siteId || '',
    kpiName: chartModal.kpiName || '',
    assetType: chartModal.assetType || 'battery_storage',
    enabled: chartModal.isOpen,
  });

  const handleExport = () => {
    if (!chartData.data || chartData.data.length === 0) return;
    exportToCSV(chartData.data, `${chartModal.siteName}_${chartModal.kpiName}_${chartData.timeRange}`, {
      timestamp: 'Timestamp', value: chartData.metadata?.displayName || 'Value', unit: 'Unit',
    });
  };

  return (
    <>
      <SiteDetailTabs
        siteId={siteId}
        site={site}
        /* djin-cloud#408: badge = Needs Attention (active AND unacked), not
           total_active (which counted acked-but-uncleared rows). */
        alarmCount={alarmSummary.unacknowledged_count}
        activeInPlaceTab={activeTab}
        onInPlaceTabChange={setActiveTab}
      />

      <div className={activeTab === 'site-overview' || activeTab === 'sld' ? '' : 'p-6'}>
        {activeTab === 'site-overview' && (
          <SiteOverviewDashboard siteId={siteId} site={site} alarmSummary={alarmSummary} kpiValues={kpiValues} kpis={kpis} generators={generators} />
        )}

        {activeTab === 'sld' && (
          <OneLineDiagramTab
            siteId={siteId}
            sldConfig={site.sld_config ?? undefined}
            onNavigateToTab={setActiveTab}
          />
        )}

        {activeTab === 'monthly-summary' && (
          <MonthlySummaryTab siteId={siteId} />
        )}

        {activeTab === 'statistics' && (
          <StatisticsTab siteId={siteId} />
        )}

        {activeTab === 'local-access' && userRole && ROLES_WITH_LOCAL_ACCESS.includes(userRole as Role) && (
          <LocalAccessTab siteId={siteId} localUrl={site.local_url} />
        )}
      </div>

      {chartModal.isOpen && chartModal.kpiName && chartModal.assetType && chartData.metadata && (
        <ChartModal
          isOpen={chartModal.isOpen}
          onClose={handleCloseModal}
          title={chartData.metadata.displayName}
          subtitle={`${chartModal.siteName} - ${chartModal.assetType.replace('_', ' ').toUpperCase()}`}
          lastUpdated={new Date()}
          onExport={handleExport}
          data={chartData.data}
          loading={chartData.isLoading}
          statistics={chartData.statistics}
        >
          <DynamicKPIChart
            data={chartData.data}
            metadata={chartData.metadata}
            timeRange={chartData.timeRange}
            setTimeRange={chartData.setTimeRange}
            loading={chartData.isLoading}
          />
        </ChartModal>
      )}
    </>
  );
}
